import Fuse from "fuse.js";
import { Search, SortAsc, SortDesc, X } from "lucide-react";
import * as React from "react";
import type { Control, FieldValues, Path } from "react-hook-form";
import { useController } from "react-hook-form";
import { Button } from "@patient-watch/ui/button";
import { Label } from "@patient-watch/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@patient-watch/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@patient-watch/ui/drawer";
import { useMediaQuery } from "./hooks/useMediaQuery";
import { cn } from "@patient-watch/ui";
import type { ExpandedSchedule } from "@patient-watch/types";
import { dedupeSchedulesByTimings } from "./dedupeSchedulesByTimings";
import { formatScheduleSummary } from "./formatScheduleTimepointsList";
import { isOwnedBuilderDraft } from "./ownedBuilderDraft";

function getScheduleLabel(schedule: ExpandedSchedule) {
    return formatScheduleSummary(schedule) || "Schedule";
}

interface ScheduleSelectDialogProps<T extends FieldValues> {
    name: Path<T>;
    control: Control<T>;
    options: ExpandedSchedule[];
    addOption?: React.ReactNode;
    currentUserId?: string | null;
    label?: string;
    labelClassName?: string;
    description?: React.ReactNode;
    placeholder?: string;
    disabled?: boolean;
    /** Tighter spacing for dense dialogs (e.g. questionnaire lists). */
    compact?: boolean;
    /** App locale tag, e.g. "de-DE". Defaults to English copy. */
    locale?: string;
}

/** ponytail: inline copy - packages/forms has no i18n dependency, callers pass their locale. */
function dialogCopy(locale?: string) {
    if (locale === "de-DE") {
        return {
            placeholder: "Zeitplan auswählen",
            title: "Zeitplan auswählen",
            search: "Zeitpläne suchen…",
            sortLabel: (order: string) => `Sortierung: ${order}`,
            sortTooltip: (order: string) => `Alphabetisch sortieren (${order})`,
            empty: "Keine Zeitpläne gefunden.",
            yours: "Deins",
            clear: "Löschen",
        };
    }
    if (locale === "fr-FR") {
        return {
            placeholder: "Sélectionner un calendrier",
            title: "Sélectionner un calendrier",
            search: "Rechercher des calendriers…",
            sortLabel: (order: string) => `Tri : ${order}`,
            sortTooltip: (order: string) => `Trier par ordre alphabétique (${order})`,
            empty: "Aucun calendrier trouvé.",
            yours: "Le vôtre",
            clear: "Effacer",
        };
    }
    if (locale === "it-IT") {
        return {
            placeholder: "Seleziona un calendario",
            title: "Seleziona un calendario",
            search: "Cerca calendari…",
            sortLabel: (order: string) => `Ordine: ${order}`,
            sortTooltip: (order: string) => `Ordina alfabeticamente (${order})`,
            empty: "Nessun calendario trovato.",
            yours: "Il Suo",
            clear: "Cancella",
        };
    }
    if (locale === "es-ES") {
        return {
            placeholder: "Seleccionar un calendario",
            title: "Seleccionar un calendario",
            search: "Buscar calendarios…",
            sortLabel: (order: string) => `Orden: ${order}`,
            sortTooltip: (order: string) => `Ordenar alfabéticamente (${order})`,
            empty: "No se han encontrado calendarios.",
            yours: "El suyo",
            clear: "Borrar",
        };
    }
    if (locale === "da-DK") {
        return {
            placeholder: "Vælg tidsplan",
            title: "Vælg tidsplan",
            search: "Søg i tidsplaner…",
            sortLabel: (order: string) => `Sortering: ${order}`,
            sortTooltip: (order: string) => `Sortér alfabetisk (${order})`,
            empty: "Ingen tidsplaner fundet.",
            yours: "Din",
            clear: "Ryd",
        };
    }
    return {
        placeholder: "Select a schedule",
        title: "Select a Schedule",
        search: "Search schedules...",
        sortLabel: (order: string) => `Sort: ${order}`,
        sortTooltip: (order: string) => `Sort alphabetically (${order})`,
        empty: "No schedules found.",
        yours: "Yours",
        clear: "Clear",
    };
}

export function ScheduleSelectDialog<T extends FieldValues>({
    name,
    control,
    options,
    addOption,
    currentUserId,
    label,
    labelClassName,
    description,
    placeholder,
    disabled,
    compact = false,
    locale,
}: ScheduleSelectDialogProps<T>) {
    const copy = dialogCopy(locale);
    const {
        field: { value, onChange },
        fieldState: { error },
    } = useController({ name, control });
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState("");
    const [alphaAsc, setAlphaAsc] = React.useState(true);
    const isMobile = useMediaQuery("(max-width: 640px)");

    const uniqueOptions = React.useMemo(
        () => dedupeSchedulesByTimings(options, typeof value === "string" ? value : null),
        [options, value]
    );

    const getDisplayName = React.useCallback((schedule: ExpandedSchedule) => getScheduleLabel(schedule), []);

    const fuse = React.useMemo(
        () =>
            new Fuse(uniqueOptions, {
                keys: ["schedule_name", { name: "derivedName", getFn: (s) => formatScheduleSummary(s) }],
                threshold: 0.4,
            }),
        [uniqueOptions]
    );

    let filtered = search ? fuse.search(search).map((r) => r.item) : uniqueOptions;
    filtered = [...filtered].sort((a, b) => {
        const ownedA = isOwnedBuilderDraft({
            authorId: a.author,
            currentUserId,
            isPublished: a.is_published,
        });
        const ownedB = isOwnedBuilderDraft({
            authorId: b.author,
            currentUserId,
            isPublished: b.is_published,
        });
        if (ownedA !== ownedB) return ownedA ? -1 : 1;
        const aName = getDisplayName(a);
        const bName = getDisplayName(b);
        const comparison = aName.localeCompare(bName, undefined, { sensitivity: "base" });
        return alphaAsc ? comparison : -comparison;
    });

    function handleSelect(schedule_id: string) {
        onChange(schedule_id);
        setOpen(false);
    }

    function handleClear(event?: React.MouseEvent) {
        event?.preventDefault();
        event?.stopPropagation();
        onChange("");
        setOpen(false);
    }

    const selected = options.find((o) => o.schedule_id === value);
    const selectedLabel = selected ? getScheduleLabel(selected) : null;

    const Content = (
        <div className={cn("flex flex-col w-full", compact ? "gap-1.5" : "gap-4")}>
            {(label || description) && (
                <div className="flex flex-col gap-0.5">
                    {label && <Label className={labelClassName}>{label}</Label>}
                    {description && <span className="text-slate-500 text-xs">{description}</span>}
                </div>
            )}
            <Button
                type="button"
                data-testid={`${name}-trigger`}
                variant="outline"
                className={cn(
                    "justify-between bg-white px-3 border-slate-300 rounded-md focus-visible:ring-2 focus-visible:ring-indigo-300 ring-offset-white focus-visible:ring-offset-2 w-full min-w-0 overflow-hidden text-sm text-left",
                    compact ? "h-10" : "h-12",
                    !selected && "text-slate-500"
                )}
                onClick={() => setOpen(true)}
                disabled={disabled}
            >
                <span className="min-w-0 font-medium text-sm truncate">
                    {selectedLabel ?? placeholder ?? copy.placeholder}
                </span>
                <Search className="ml-2 w-4 h-4 shrink-0" />
            </Button>
            {error && <span className="text-red-500 text-xs">{error.message}</span>}
        </div>
    );

    const OptionCard = (option: ExpandedSchedule) => {
        const title = getScheduleLabel(option);
        const isSelected = value === option.schedule_id;

        return (
            <button
                key={option.schedule_id}
                type="button"
                data-testid={`schedule-option-${option.schedule_name.toLowerCase().replace(/\s+/g, "-")}`}
                className={cn(
                    "relative flex flex-col hover:bg-slate-50 dark:hover:bg-slate-900 p-3 border rounded-lg w-full text-left transition",
                    isSelected && "border-indigo-600 ring-2 ring-indigo-100 shadow-sm bg-indigo-50/40"
                )}
                onClick={() => handleSelect(option.schedule_id)}
            >
                <span className={cn("font-semibold text-sm leading-snug", isSelected && "text-indigo-700")}>
                    {title}
                </span>
                {isOwnedBuilderDraft({
                    authorId: option.author,
                    currentUserId,
                    isPublished: option.is_published,
                }) ? (
                    <span
                        className="mt-1 inline-flex w-fit items-center rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 font-medium text-indigo-800 text-xs"
                        data-testid="builder-yours-badge"
                    >
                        {copy.yours}
                    </span>
                ) : null}
            </button>
        );
    };

    const sortOrder = alphaAsc ? "A-Z" : "Z-A";
    const sortLabel = copy.sortLabel(sortOrder);
    const sortTooltip = copy.sortTooltip(sortOrder);

    const List = (
        <>
            <div className="flex items-center gap-2 mb-2">
                <div className="relative flex-1">
                    <input
                        type="text"
                        placeholder={copy.search}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full text-sm"
                    />
                    <Search className="top-1/2 right-2 absolute w-4 h-4 text-slate-500 -translate-y-1/2 pointer-events-none" />
                </div>
                <div className="flex items-center gap-1">
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setAlphaAsc((asc) => !asc)}
                        title={sortTooltip}
                        aria-label={sortTooltip}
                    >
                        {alphaAsc ? <SortAsc className="w-5 h-5" /> : <SortDesc className="w-5 h-5" />}
                    </Button>
                    <span className="min-w-[80px] text-slate-500 text-xs" title={sortTooltip}>
                        {sortLabel}
                    </span>
                </div>
            </div>
            <div className="flex-1 -mx-1 px-1 min-h-0 max-h-[60vh] overflow-y-auto">
                <div className="flex flex-col gap-2">
                    {filtered.length === 0 && <div className="py-8 text-slate-400 text-center">{copy.empty}</div>}
                    {filtered.map(OptionCard)}
                </div>
            </div>
            <div className="flex items-center gap-2 mt-2 pt-2 border-slate-200 border-t">
                {addOption ? <div className="flex flex-1 min-w-0">{addOption}</div> : null}
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={handleClear}
                    disabled={value == null || value === ""}
                >
                    <X className="w-4 h-4" />
                    {copy.clear}
                </Button>
            </div>
        </>
    );

    return (
        <>
            {Content}
            {isMobile ? (
                <Drawer open={open} onOpenChange={setOpen}>
                    <DrawerContent>
                        <DrawerHeader>
                            <DrawerTitle>{copy.title}</DrawerTitle>
                        </DrawerHeader>
                        <div className="p-4">{List}</div>
                    </DrawerContent>
                </Drawer>
            ) : (
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogContent className="flex flex-col p-4 md:p-6 max-w-lg max-h-[85vh] overflow-hidden">
                        <DialogHeader className="shrink-0">
                            <DialogTitle>{copy.title}</DialogTitle>
                        </DialogHeader>
                        <div className="flex flex-col flex-1 gap-4 min-h-0">{List}</div>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
}
